import { type SyntheticEvent, useCallback, useEffect, useState } from 'react';

import { useInjection } from 'inversify-react';

import styled from 'styled-components';

import { Box, Tabs, Tab } from '@mui/material';
import { TYPES } from '@/engine/ioc';
import { CORE_TYPES } from '@/engine/app/modules/core';
import { ViewOptionsTable } from '@/components/viewOption/ViewOptionsTable.tsx';
import { ToolSelector } from '@/engine/app/modules/indications/ToolSelector.tsx';
import { IndicationsContainer } from '@/components/contentContainers/IndicationsContainer.tsx';

import type { FC, PropsWithChildren } from 'react';
import type { ViewOptionsController } from '@/components/viewOption';
import { type Theme, useTheme } from '@mui/material/styles';
import type { ViewsService } from '@/engine/app/Views.service.ts';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import { type IReactionDisposer, reaction } from 'mobx';
import { useTabsData } from './useTabsData';

interface IProps extends PropsWithChildren {
  marginTop: number;
}

export const rightPanelTabContainerRenderingError = 'View options should be defined';

export const RightPanelTabContainer: FC<IProps> = ({ children, marginTop }) => {
  const controller: ViewOptionsController = useInjection<ViewOptionsController>(TYPES.ViewOptionsController);
  const loggerService: ObservabilityLoggerService = useInjection<ObservabilityLoggerService>(
    CORE_TYPES.ObservabilityLoggerService
  );

  const viewsService: ViewsService = useInjection<ViewsService>(TYPES.ViewsService);
  const theme: Theme = useTheme();
  const [value, setValue] = useState(0);

  const tabsData = useTabsData();

  const updateTab = useCallback(
    (value: number): void => {
      setValue(value);
      loggerService.setGlobalContextProperty('tabName', tabsData[value].label);
    },
    [loggerService, tabsData]
  );

  useEffect(() => {
    const disposer: IReactionDisposer = reaction(
      () => viewsService.currentView,
      (): void => {
        updateTab(0);
      }
    );

    return (): void => disposer();
  }, [viewsService, updateTab]);

  const viewOptions = controller.viewOptions;
  if (!viewOptions || viewOptions.length == 0) {
    throw new Error(rightPanelTabContainerRenderingError);
  }

  const handleChange = (_: SyntheticEvent, newValue: number): void => {
    updateTab(newValue);
  };

  return (
    <Box sx={{ borderBottom: 0, borderColor: 'divider' }}>
      <StyledTabs
        data-testid={'rightPanelTabs'}
        value={value}
        onChange={handleChange}
        aria-label="right-panel-tabs"
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'sticky',
          background: `${theme.customPalette.panels.side.background}`,
          top: marginTop - 5,
          zIndex: 5,
          minHeight: '3em'
        }}
      >
        {tabsData.map((tab, index) => {
          return (
            <Tab
              sx={{
                color: theme.customPalette.panels.side.font,
                fontSize: '0.8rem'
              }}
              data-testid={tab.label}
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition={'start'}
              id={`tab-${index}`}
              aria-controls={`tabpanel-${index}`}
            />
          );
        })}
      </StyledTabs>
      <TabContainer id={'tabpanel-0'} aria-labelledby={'tab-0'} hidden={value !== 0}>
        {children}
      </TabContainer>
      <TabContainer id={'tabpanel-1'} aria-labelledby={'tab-1'} hidden={value !== 1}>
        <ViewOptionsTable controller={controller} />
      </TabContainer>
      <TabContainer id={'tabpanel-2'} aria-labelledby={'tab-2'} hidden={value !== 2}>
        <ToolSelector orientation={'horizontal'} />
        <IndicationsContainer />
      </TabContainer>
    </Box>
  );
};

const TabContainer = styled.div`
  margin-top: 0.625rem;
`;

const StyledTabs = styled(Tabs)`
  div[role='tablist'] {
    justify-content: space-around;
  }

  button {
    padding: 0;
    justify-content: flex-start;
    min-height: 2rem;
  }
`;
